import { Zap, ArrowRight } from 'lucide-preact'
import type { ComponentChildren } from 'preact'
import { memo } from 'preact/compat'
import type { AnalyticsViewDto } from '@/types/analytics'

interface Props {
  analytics: AnalyticsViewDto | null
  loading: boolean
  error: string | null
}

/** Step-through rate between two funnel stages, or "—" when the prior stage is empty or unmeasured. */
function stepThrough(numerator: number, denominator: number | null): string {
  if (denominator === null || denominator <= 0) return '—'
  return `${Math.round((numerator / denominator) * 100)}%`
}

function Stage({ value, label, highlight }: { value: number | null; label: string; highlight?: boolean }) {
  return (
    <div className="flex flex-col items-center min-w-0">
      <span className={`text-base font-bold leading-none tabular-nums ${highlight ? 'text-green-600 dark:text-green-400' : 'text-slate-900 dark:text-white'}`}>
        {value === null ? '—' : value.toLocaleString()}
      </span>
      <span className="text-2xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-1">{label}</span>
    </div>
  )
}

function Step({ rate }: { rate: string }) {
  return (
    <div className="flex flex-col items-center text-slate-400 dark:text-slate-500 pb-4">
      <ArrowRight className="w-3.5 h-3.5" />
      <span className="text-2xs mt-0.5">{rate}</span>
    </div>
  )
}

/** Tile chrome (title + Zap icon) shared across the funnel's error / no-data / data states. */
function FunnelShell({ children }: { children: ComponentChildren }) {
  return (
    <div className="tile">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight">
          <span className="block">Visitor</span>
          <span className="block">Funnel</span>
        </h3>
        <Zap className="w-5 h-5 text-primary-600 dark:text-primary-400" />
      </div>
      {children}
    </div>
  )
}

/**
 * Visitor funnel snapshot: Unique landing page views → Conversions, with the step-through rate
 * between them. Reads the same AnalyticsViewDto the workspace tiles use — no extra fetch.
 *
 * ORB-625 cut the Thank You stage and moved the denominator off total views. Total views counts
 * reloads, so a rate computed against it understates every campaign; unique views is one count per
 * session per landing page, which is the population that could actually have converted. Total views
 * and conversions each have their own tile beside this one, so the tile's job is the rate.
 */
export const VisitorFunnelStatTile = memo(({ analytics, loading, error }: Props) => {
  if (loading) {
    return (
      <div className="tile">
        <div className="flex items-start justify-between mb-3">
          <div className="flex flex-col gap-1">
            <div className="h-3 w-16 rounded-md bg-shimmer-light animate-shimmer" />
            <div className="h-3 w-12 rounded-md bg-shimmer-light animate-shimmer" />
          </div>
          <span className="w-5 h-5 rounded-md bg-shimmer-light animate-shimmer" />
        </div>
        <div className="flex items-end justify-between gap-2 mt-4">
          <div className="h-7 w-10 rounded-md bg-shimmer-light animate-shimmer" />
          <div className="h-7 w-10 rounded-md bg-shimmer-light animate-shimmer" />
        </div>
      </div>
    )
  }

  if (error) {
    return <FunnelShell><div className="text-sm text-red-600 dark:text-red-400">{error}</div></FunnelShell>
  }

  if (!analytics) {
    return <FunnelShell><div className="text-sm text-slate-500 dark:text-slate-400">No data</div></FunnelShell>
  }

  // Absent rather than zero when the API has not sent the measure: 0 and "not measured" render as
  // different things, and only one of them is honest.
  const unique = analytics.UniqueLandingPageViews?.Current ?? null
  const conv = analytics.Conversions.Current

  return (
    <FunnelShell>
      <div className="flex items-end justify-between gap-1">
        <Stage value={unique} label="Unique" />
        <Step rate={stepThrough(conv, unique)} />
        <Stage value={conv} label="Conv" highlight />
      </div>
    </FunnelShell>
  )
})
